import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReconcilePreviewResponse } from 'types/api-responses';

// Runs one preview request for the given selection; reports the classified
// preview or an error message. Each tearsheet adapts its own mutation atom +
// URL into this shape.
export type RunReconcilePreview<P extends ReconcilePreviewResponse> = (
  ids: number[],
  onSuccess: (data: P) => void,
  onError: (message: string) => void
) => void;

// The preview protocol shared by the structuring and entity-page reconcile
// tearsheets: re-preview whenever the selection changes (≥2 selected). The
// backend classifies same-name dedupe groups vs alias survivors and returns
// the load-time fingerprints replayed on submit. Each request bumps a sequence
// token and stale responses are dropped, so an out-of-order (slow, earlier)
// response can never overwrite the current selection's classification.
//
// Owns the preview + the per-group survivor/primary-address overrides, which
// reset together (a new preview's groups make the old overrides meaningless).
export function useReconcilePreview<P extends ReconcilePreviewResponse>(
  open: boolean,
  selectedIds: Set<number>,
  runPreview: RunReconcilePreview<P>,
  onError: (message: string | null) => void
) {
  const [preview, setPreview] = useState<P | null>(null);
  const [survivingOverrides, setSurvivingOverrides] = useState<
    Record<string, number>
  >({});
  const [primaryAddressOverrides, setPrimaryAddressOverrides] = useState<
    Record<string, number>
  >({});

  const selectionKey = useMemo(
    () =>
      Array.from(selectedIds)
        .sort((a, b) => a - b)
        .join(','),
    [selectedIds]
  );

  // Bumped to re-preview the same selection (fresh fingerprints after a stale
  // submit); that refresh keeps the error explaining it on screen.
  const [refreshCount, setRefreshCount] = useState(0);
  const keepError = useRef(false);

  const previewRequestSeq = useRef(0);
  useEffect(() => {
    const clearError = !keepError.current;
    keepError.current = false;
    if (!open) return;
    const ids = selectionKey ? selectionKey.split(',').map(Number) : [];
    if (ids.length < 2) {
      previewRequestSeq.current += 1; // supersede any in-flight preview
      setPreview(null);
      return;
    }
    previewRequestSeq.current += 1;
    const seq = previewRequestSeq.current;
    if (clearError) onError(null);
    runPreview(
      ids,
      (data) => {
        if (seq !== previewRequestSeq.current) return; // superseded
        setPreview(data);
        // Reset overrides — the previous selection's groups no longer apply.
        setSurvivingOverrides({});
        setPrimaryAddressOverrides({});
      },
      (message) => {
        if (seq !== previewRequestSeq.current) return; // superseded
        onError(message);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey stands in for selectedIds
  }, [open, selectionKey, runPreview, refreshCount]);

  const resetPreview = () => {
    setPreview(null);
    setSurvivingOverrides({});
    setPrimaryAddressOverrides({});
  };

  return {
    preview,
    survivingOverrides,
    setSurvivingOverrides,
    primaryAddressOverrides,
    setPrimaryAddressOverrides,
    resetPreview,
    refreshPreview: () => {
      keepError.current = true;
      setRefreshCount((n) => n + 1);
    },
  };
}
